import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <title>文章不存在 - 51黑料吃瓜网</title>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug)
    .slice(0, 4);

  const pageTitle = `${article.title} - 51黑料吃瓜网`;
  const pageDescription = article.excerpt;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={article.tags.join(',') + ',51黑料,黑料爆料,吃瓜网'} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`https://51heiliao.com/article/${slug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://51heiliao.com/article/${slug}`} />
      <meta property="og:image" content={article.image} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:author" content={article.author} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={article.image} />

      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.publishDate,
          publisher: {
            '@type': 'Organization',
            name: '51黑料吃瓜网',
            url: 'https://51heiliao.com/',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://51heiliao.com/article/${slug}`,
          },
          articleSection: article.category,
          keywords: article.tags.join(','),
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://51heiliao.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.category,
              item: `https://51heiliao.com/category/${article.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="relative w-full h-[420px] md:h-[550px] flex items-center overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/65"></div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-[1400px] mx-auto">
              <div className="max-w-3xl">
                <span className="inline-block px-3 py-1 rounded-full border border-white/40 text-white/90 text-xs font-medium mb-4 whitespace-nowrap">
                  {article.isExclusive ? '独家' : ''} {article.isVideo ? '视频' : ''} 深度长文
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-snug mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-white/60 text-xs md:text-sm">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i>
                    {article.readCount} 阅读
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-chat-3-line"></i>
                    {article.commentCount} 评论
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <main>
          <Breadcrumb
            items={[
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="w-full py-8 md:py-12 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div className="mb-8 md:mb-12">
                <div className="text-center mb-8">
                  <span className="text-xs text-foreground-400 font-heading italic">阅读数据</span>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-2">
                    全网热度
                    <span className="font-heading italic font-bold text-primary-500 ml-2">持续引爆</span>
                  </h2>
                  <p className="text-foreground-500 text-xs mt-1">最后更新：{article.publishDate}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto">
                  <div className="bg-primary-100/80 rounded-xl p-6 relative overflow-hidden">
                    <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/30 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-primary-600"></i>
                    </span>
                    <div className="text-3xl md:text-4xl font-heading font-bold text-primary-700 mb-1">{article.readCount}+</div>
                    <p className="text-sm text-foreground-700 font-medium mb-1">累计阅读量</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">该爆料在全平台引发广泛关注</p>
                    <p className="text-xs text-foreground-400 italic mt-2">—— 全民吃瓜进行时</p>
                  </div>
                  <div className="bg-accent-100/80 rounded-xl p-6 relative overflow-hidden">
                    <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/30 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-accent-600"></i>
                    </span>
                    <div className="text-3xl md:text-4xl font-heading font-bold text-accent-700 mb-1">97%</div>
                    <p className="text-sm text-foreground-700 font-medium mb-1">讨论热度</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">社交媒体话题讨论度持续走高，引发全民热议</p>
                    <p className="text-xs text-foreground-400 italic mt-2">—— 话题引爆全网</p>
                  </div>
                </div>
              </div>

              <article
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <div className="inline-flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                  <span className="text-xs font-medium text-accent-700 whitespace-nowrap">吃瓜热评</span>
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                  精选评论
                </h2>
                <p className="text-foreground-500 text-sm">来看看吃瓜群众们都怎么说</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[1200px] mx-auto">
                {[
                  { user: '吃瓜第一线', role: '认证用户', text: '这个瓜也太炸了吧！之前就觉得不对劲，没想到真相这么劲爆。51黑料的独家报道真的每次都能挖到最深的料！', rating: 5 },
                  { user: '娱乐观察员', role: '自媒体作者', text: '分析得很透彻，每个细节都有据可查。比起那些只会编故事的号，51黑料的专业度高出太多了。', rating: 5 },
                  { user: '深夜吃瓜人', role: '资深用户', text: '这篇文章的信息量太大了，看了三遍才消化完。期待后续的跟踪报道，加油！', rating: 4 },
                ].map((comment, i) => (
                  <div key={i} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <div className="flex gap-0.5 mb-2">
                      {Array.from({ length: comment.rating }).map((_, j) => (
                        <span key={j} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3">{comment.text}</p>
                    <div className="flex items-center gap-2 pt-3 border-t border-background-200">
                      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-primary-600">{comment.user.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-800">{comment.user}</p>
                        <p className="text-xs text-foreground-400">{comment.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="w-full py-10 md:py-14 bg-background-50">
              <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                <div className="text-center mb-8 md:mb-10">
                  <span className="inline-block px-4 py-1.5 rounded-full border border-background-300 text-foreground-600 text-xs font-medium mb-2 whitespace-nowrap">
                    相关推荐
                  </span>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                    更多{article.category}爆料
                  </h2>
                  <p className="text-foreground-500 text-sm">你可能还对这些爆料感兴趣</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full aspect-[4/3] overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
                          {rel.title}
                        </h3>
                        <span className="text-xs text-foreground-400">{rel.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="relative w-full h-[400px] md:h-[480px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20dramatic%20news%20backdrop%20with%20soft%20amber%20and%20coral%20gradient%2C%20cinematic%20depth%20of%20field%2C%20luxurious%20golden%20light%20rays%2C%20editorial%20magazine%20quality%2C%20rich%20atmospheric%20textures%2C%20elegant%20bokeh%2C%20dramatic%20warm%20composition&width=1800&height=1200&seq=cta-subscribe&orientation=landscape"
              alt="订阅提醒"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
            <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-3 tracking-wide">
                不想错过下一个瓜？
              </h2>
              <p className="text-white/70 text-sm md:text-base mb-8 leading-relaxed">
                订阅51黑料吃瓜网，第一时间收到最新爆料推送
                <br />
                每日黑料大赛实时提醒，吃瓜永不打烊
              </p>
              <a
                href="#subscribe"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-foreground-950 text-white hover:bg-foreground-900 font-semibold text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center">
                  <i className="ri-notification-3-line text-white text-xs"></i>
                </span>
                订阅推送提醒
                <i className="ri-arrow-right-up-line ml-1"></i>
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}